// The widget renderer. The SAME code runs on installers' websites (with the real
// API) and in the console's branding preview (with mockApi), so the preview is
// an exact replica of what homeowners see.
//
// Framework-free on purpose: the production bundle stays small and can't clash
// with whatever the host website uses. Everything renders into a shadow root.

import { ensureGoogleFont } from "./fonts";
import { mountContactForm } from "./contact";
import { mountFullWidget } from "./full";
import { METHOD_STATEMENT } from "./method";
import { WIDGET_CSS, cssVars } from "./styles";
import type { Address, ContactStep, FullStep, PropertyDetails, PropertyLookup, Quote, SolarStep, Step, WidgetApi, WidgetBranding, WidgetProduct } from "./types";
import { mountSolarWidget } from "./solar/solar";
import { STEPS } from "./types";

export interface MountOptions {
  branding: WidgetBranding;
  api: WidgetApi;
  /** Preview only: pin a floating widget to the host element instead of the window. */
  contained?: boolean;
  /** Preview only: outline + label the floating widget so it stands out. */
  highlight?: boolean;
  /** Called whenever the visible step changes (the preview keeps its step bar in sync). */
  onStep?: (step: Step | FullStep | ContactStep | SolarStep) => void;
  /** Floating mode: start with the panel open (the preview does). */
  startOpen?: boolean;
  /** Full width / hosted: offer "Get my detailed quote" after the estimate (session.quotes). */
  quotes?: boolean;
  /** Which product this widget is (missing = "ashp"). Solar mounts the roof-scan widget. */
  product?: WidgetProduct;
  /** Solar: the Maps JavaScript browser key (session.maps.browserKey). */
  mapsKey?: string;
}

export interface WidgetHandle {
  /** Re-style with new branding, keeping the homeowner's progress. */
  update(branding: WidgetBranding): void;
  /** Preview only: show a step, filling earlier steps with sample data. */
  goTo(step: Step | FullStep | ContactStep | SolarStep): Promise<void>;
  destroy(): void;
  /** Solar preview: the draft prices changed, so ask the backend for the figures again. */
  refresh?(): void;
}

// Choices offered on the "property" step
const PROPERTY_TYPES: [PropertyDetails["propertyType"], string][] = [
  ["detached", "Detached"],
  ["semi-detached", "Semi-detached"],
  ["terraced", "Terraced"],
  ["bungalow", "Bungalow"],
  ["flat", "Flat"],
];
const HEATING: [PropertyDetails["heating"], string][] = [
  ["gas", "Gas boiler"],
  ["oil", "Oil"],
  ["lpg", "LPG"],
  ["electric", "Electric"],
  ["other", "Other"],
];

/** All three "About your home" answers given (from the EPC or the homeowner). */
function isComplete(d: Partial<PropertyDetails>): d is PropertyDetails {
  return d.propertyType !== undefined && d.bedrooms !== undefined && d.heating !== undefined;
}

const POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** HTML-escape installer- and homeowner-supplied text. */
function esc(s: string | number): string {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

const gbp = (n: number) => `£${Math.round(n).toLocaleString("en-GB")}`;

// Small inline icons (no external requests from customers' sites)
const ICON_HOME =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 10.5 12 3l9 7.5V21a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z"/></svg>';
const ICON_CHECK =
  '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5 10 17l9-10"/></svg>';
// Simplified xenscope two-leaf glyph
const XENSCOPE_GLYPH =
  '<svg viewBox="0 0 10 18" aria-hidden="true"><path d="M0 0 6.2 3.6Q10 5.8 10 9.2V9.6H0Z" fill="#E1EE2B"/><rect y="11" width="10" height="7" rx="2.4" fill="#014852"/></svg>';

interface State {
  step: Step;
  open: boolean; // floating panel visible
  busy: boolean;
  error: string | null;
  postcode: string;
  addresses: Address[];
  address: Address | null;
  details: Partial<PropertyDetails>; // EPC pre-fill + homeowner edits; all three needed to continue
  lookup: PropertyLookup | null | undefined; // EPC result for the address (undefined = not looked up yet)
  quote: Quote | null;
  wantsQuote: boolean; // "Get my detailed quote" chosen (not a callback)
  quoteUrl: string | null; // the quote page, once sent
  name: string;
  email: string;
  phone: string;
  consent: boolean;
}

/**
 * Mount a widget: the small card or the Full quote page, per branding.layout.style.
 * Switching style in the console preview remounts the other renderer.
 */
export function mountWidget(host: HTMLElement, opts: MountOptions): WidgetHandle {
  // Solar widgets are their own renderer (a map stage with a floating step card):
  // docs/12-solar-roof-scan.md. Compact isn't offered for solar; every style mounts it.
  if (opts.product === "solar") {
    const solar = mountSolarWidget(host, {
      branding: opts.branding,
      api: opts.api,
      mapsKey: opts.mapsKey,
      contained: opts.contained,
      highlight: opts.highlight,
      startOpen: opts.startOpen,
      onStep: opts.onStep,
    });
    return solar as WidgetHandle;
  }
  // Hosted pages show the Full width widget between the installer's header and footer
  // The contact form has its own renderer (compact look, one form)
  const styleOf = (b: WidgetBranding) => (b.layout.style === "full" || b.layout.style === "hosted" ? "full" : b.layout.style === "contact" ? "contact" : "small");
  let style = styleOf(opts.branding);
  const mount = (b: WidgetBranding): WidgetHandle =>
    style === "full"
      ? (mountFullWidget(host, { ...opts, branding: b, onStep: opts.onStep, quotes: opts.quotes }) as WidgetHandle)
      : style === "contact"
        ? mountContactForm(host, { ...opts, branding: b })
        : mountSmall(host, { ...opts, branding: b });
  let inner = mount(opts.branding);
  return {
    update(next) {
      if (styleOf(next) !== style) {
        inner.destroy();
        style = styleOf(next);
        inner = mount(next);
      } else inner.update(next);
    },
    goTo: (step) => inner.goTo(step),
    destroy: () => inner.destroy(),
  };
}

function mountSmall(host: HTMLElement, opts: MountOptions): WidgetHandle {
  let branding = opts.branding;
  const api = opts.api;

  // Shadow root isolates our CSS/markup from the host page (reused on remount)
  const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });
  shadow.innerHTML = "";
  const style = document.createElement("style");
  style.textContent = WIDGET_CSS;
  const root = document.createElement("div");
  shadow.append(style, root);

  const state: State = {
    step: "postcode",
    open: opts.startOpen ?? false,
    busy: false,
    error: null,
    postcode: "",
    addresses: [],
    address: null,
    details: {},
    lookup: undefined,
    quote: null,
    wantsQuote: false,
    quoteUrl: null,
    name: "",
    email: "",
    phone: "",
    consent: false,
  };

  // QuoteFlow: offer a detailed quote when a price was shown and the home has an EPC
  // (the quote's answers come from the certificate)
  const canQuote = () => Boolean(opts.quotes && api.requestQuote && state.lookup);

  // ─── Rendering ─────────────────────────────────────────────────────────────

  function applyTheme() {
    ensureGoogleFont(branding.font.family);
    const vars = cssVars(branding);
    for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v);
    root.className = `xq-root${opts.contained ? " xq-contained" : ""}${opts.highlight ? " xq-highlight" : ""}`;
  }

  let renderedStep: Step | null = null;

  function render() {
    applyTheme();
    // Re-rendering the same step (e.g. a chip chosen, a restyle) keeps scroll
    // positions: the canvas body (fixed-height widgets) and the address list
    const sameStep = renderedStep === state.step;
    const bodyTop = sameStep ? root.querySelector(".xq-body")?.scrollTop ?? 0 : 0;
    const listTop = sameStep ? root.querySelector(".xq-list")?.scrollTop ?? 0 : 0;

    const l = branding.layout;
    if (l.mode === "floating") {
      root.innerHTML = `<div class="xq-floating xq-pos-${l.position}">
        ${state.open ? canvas() : `<button type="button" class="xq-launcher" data-action="open">${ICON_HOME}${esc(l.launcherLabel)}</button>`}
      </div>`;
    } else {
      root.innerHTML = `<div class="xq-inline xq-align-${l.align}">${canvas()}</div>`;
    }

    if (sameStep) {
      const body = root.querySelector(".xq-body");
      const list = root.querySelector(".xq-list");
      if (body) body.scrollTop = bodyTop;
      if (list) list.scrollTop = listTop;
    }
    renderedStep = state.step;
    syncDisabled();
  }

  function canvas(): string {
    const c = branding.copy;
    const idx = STEPS.indexOf(state.step);
    const pct = Math.round((idx / (STEPS.length - 1)) * 100);
    const logo = branding.logoUrl && /^https:\/\//.test(branding.logoUrl) ? `<img class="xq-logo" src="${esc(branding.logoUrl)}" alt="">` : "";
    const close = branding.layout.mode === "floating" ? `<button type="button" class="xq-close" data-action="close" aria-label="Close">×</button>` : "";
    const head = logo || close ? `<div class="xq-head">${logo}${close}</div>` : "";
    return `<div class="xq-canvas" role="region" aria-label="${esc(c.title)}">
      <div class="xq-body">
        ${head}
        <div class="xq-progress" aria-hidden="true"><span style="width:${pct}%"></span></div>
        ${stepHtml()}
        ${state.error ? `<p class="xq-error" role="alert">${esc(state.error)}</p>` : ""}
      </div>
      <div class="xq-foot">Powered by <a href="https://xenscope.com" target="_blank" rel="noopener">${XENSCOPE_GLYPH}xenscope</a></div>
    </div>`;
  }

  function stepHtml(): string {
    const c = branding.copy;
    switch (state.step) {
      case "postcode":
        return `<h2 class="xq-title">${esc(c.title)}</h2>
          <p class="xq-sub">${esc(c.subtitle)}</p>
          <form data-form="postcode" novalidate>
            <label class="xq-label" for="xq-postcode">Postcode</label>
            <div class="xq-row">
              <input class="xq-input" id="xq-postcode" data-model="postcode" value="${esc(state.postcode)}" placeholder="e.g. SW1A 1AA" autocomplete="postal-code">
              <button type="submit" class="xq-btn" data-needs="postcode" ${state.busy ? "disabled" : ""}>${state.busy ? "Searching…" : esc(c.buttonText)}</button>
            </div>
          </form>`;

      case "address":
        return `<h2 class="xq-title">Select your address</h2>
          <p class="xq-sub">${esc(state.addresses.length)} addresses found for ${esc(state.postcode.toUpperCase())}.</p>
          <div class="xq-list">
            ${state.addresses
              .map((a) => `<button type="button" class="xq-option" data-action="pick-address" data-id="${esc(a.id)}" aria-pressed="${state.address?.id === a.id}">${esc(a.line)}</button>`)
              .join("")}
          </div>
          <button type="button" class="xq-btn xq-btn-block" data-action="to-details" data-needs="address">${state.busy ? "Checking your home…" : "Continue"}</button>
          <p class="xq-hint"><button type="button" class="xq-link" data-action="back">Change postcode</button></p>`;

      case "details": {
        const d = state.details;
        const epc = state.lookup?.details ?? {};
        const hasEpc = Object.keys(epc).length > 0;
        // One chip group. The EPC's answer gets an "EPC" tag (whether or not it is
        // still selected) so the homeowner can see what the certificate said.
        const chips = (field: keyof PropertyDetails, options: [string, string][]) => {
          const current = d[field] === undefined ? undefined : String(d[field]);
          const fromEpc = epc[field] === undefined ? undefined : String(epc[field]);
          return `<div class="xq-chips" role="group">${options
            .map(([v, label]) => {
              const isEpc = v === fromEpc;
              return `<button type="button" class="xq-chip${isEpc ? " xq-chip-epc" : ""}" data-action="chip" data-field="${field}" data-value="${v}" aria-pressed="${current === v}"${isEpc ? ' title="From your energy certificate (EPC)"' : ""}>${label}${isEpc ? '<span class="xq-epc-tag">EPC</span>' : ""}</button>`;
            })
            .join("")}</div>`;
        };
        // "From EPC" marker next to a question whose answer still matches the certificate
        const label = (text: string, field: keyof PropertyDetails) =>
          `<span class="xq-label">${text}${epc[field] !== undefined && d[field] === epc[field] ? ' <span class="xq-from-epc">From your EPC</span>' : ""}</span>`;
        const facts = [state.lookup?.epcRating && `EPC rating ${esc(state.lookup.epcRating)}`, state.lookup?.floorArea && `${esc(state.lookup.floorArea)} m²`]
          .filter(Boolean)
          .join(" · ");
        return `<h2 class="xq-title">About your home</h2>
          <p class="xq-sub">${esc(state.address?.line ?? "")}</p>
          ${
            hasEpc
              ? `<div class="xq-epc-note"><strong>We've filled this in from your home's energy certificate${facts ? ` (${facts})` : ""}.</strong> Change anything that's not right.</div>`
              : `<div class="xq-epc-note xq-epc-none">We couldn't find an energy certificate for this address, so please tell us a bit about your home.</div>`
          }
          <div class="xq-field">${label("Property type", "propertyType")}${chips("propertyType", PROPERTY_TYPES)}</div>
          <div class="xq-field">${label("Bedrooms", "bedrooms")}${chips("bedrooms", [["1", "1"], ["2", "2"], ["3", "3"], ["4", "4"], ["5", "5+"]])}</div>
          <div class="xq-field">${label("Current heating", "heating")}${chips("heating", HEATING)}</div>
          <button type="button" class="xq-btn xq-btn-block" data-action="to-quote" data-needs="details">${state.busy ? "Calculating…" : "See my estimate"}</button>
          <p class="xq-hint"><button type="button" class="xq-link" data-action="back">Back</button></p>`;
      }

      case "quote": {
        const q = state.quote!;
        // No usable energy certificate: the published method can't estimate it online (docs/03)
        if (q.noEstimate) {
          return `<h2 class="xq-title">${esc(c.quoteTitle)}</h2>
          <div class="xq-quote">
            <div class="xq-quote-meta">We couldn't find a usable energy certificate for your home, so we can't estimate it online. We'll recommend the right system and price after a short home survey.</div>
          </div>
          <button type="button" class="xq-btn xq-btn-block" data-action="to-contact">Arrange my survey</button>
          <details class="xq-method"><summary>How are estimates worked out?</summary><p>${esc(METHOD_STATEMENT)}</p></details>`;
        }
        // No size the installer offers covers this home: no price, survey instead
        if (q.tooBig) {
          return `<h2 class="xq-title">${esc(c.quoteTitle)}</h2>
          <div class="xq-quote">
            <div class="xq-quote-meta">Your home needs a larger heat pump than we can price online (more than ${esc(q.heatPumpKw)} kW). We'll recommend the right system after a survey.</div>
          </div>
          <dl class="xq-facts">
            <dt>Estimated heat loss</dt><dd>${esc(q.requiredKw ?? "")} kW</dd>
            <dt>Home</dt><dd>${esc(state.address?.line ?? "")}</dd>
          </dl>
          <button type="button" class="xq-btn xq-btn-block" data-action="to-contact">Get my exact price</button>
          <details class="xq-method"><summary>How is this estimate worked out?</summary><p>${esc(METHOD_STATEMENT)}</p></details>`;
        }
        return `<h2 class="xq-title">${esc(c.quoteTitle)}</h2>
          <div class="xq-quote">
            <div class="xq-quote-range">${gbp(q.low)} – ${gbp(q.high)}</div>
            <div class="xq-quote-meta">After the ${gbp(q.grant)} Boiler Upgrade Scheme grant</div>
          </div>
          <dl class="xq-facts">
            ${q.requiredKw ? `<dt>Estimated heat loss</dt><dd>${esc(q.requiredKw)} kW</dd>` : ""}
            <dt>Heat pump</dt><dd>${esc(q.heatPumpKw)} kW air source</dd>
            <dt>Home</dt><dd>${esc(state.address?.line ?? "")}</dd>
          </dl>
          ${
            canQuote()
              ? `<button type="button" class="xq-btn xq-btn-block" data-action="to-quote-contact">Get my detailed quote</button>
                 <button type="button" class="xq-btn xq-btn-block xq-btn-ghost" data-action="to-contact">Request a callback</button>
                 <p class="xq-hint">Your detailed quote shows the exact system, fixed prices and running costs, straight away. It's subject to a home survey.</p>`
              : `<button type="button" class="xq-btn xq-btn-block" data-action="to-contact">Get my exact price</button>`
          }
          <p class="xq-hint">${esc(c.disclaimer)}</p>
          <details class="xq-method"><summary>How is this estimate worked out?</summary><p>${esc(METHOD_STATEMENT)}</p></details>`;
      }

      case "contact":
        return `<h2 class="xq-title">${state.wantsQuote ? "Get your detailed quote" : "Where should we send your quote?"}</h2>
          <p class="xq-sub">${state.wantsQuote ? "We'll show it to you now and email you a copy. It's subject to a home survey." : "We'll confirm your price and arrange a home survey."}</p>
          <form data-form="contact" novalidate>
            <div class="xq-field"><label class="xq-label" for="xq-name">Name</label>
              <input class="xq-input" id="xq-name" data-model="name" value="${esc(state.name)}" autocomplete="name"></div>
            <div class="xq-field"><label class="xq-label" for="xq-email">Email</label>
              <input class="xq-input" id="xq-email" type="email" data-model="email" value="${esc(state.email)}" autocomplete="email"></div>
            <div class="xq-field"><label class="xq-label" for="xq-phone">Phone (optional)</label>
              <input class="xq-input" id="xq-phone" type="tel" data-model="phone" value="${esc(state.phone)}" autocomplete="tel"></div>
            <label class="xq-consent"><input type="checkbox" data-model="consent" ${state.consent ? "checked" : ""}><span>${esc(c.consentText)}</span></label>
            <button type="submit" class="xq-btn xq-btn-block" data-needs="contact" ${state.busy ? "disabled" : ""}>${state.busy ? (state.wantsQuote ? "Preparing your quote…" : "Sending…") : state.wantsQuote ? "Get my quote" : "Send my quote"}</button>
            ${state.wantsQuote ? '<p class="xq-hint">Your installer keeps your name and contact details with your quote.</p>' : ""}
          </form>`;

      case "done":
        if (state.quoteUrl) {
          return `<div class="xq-done">
            <div class="xq-done-icon">${ICON_CHECK}</div>
            <h2 class="xq-title">Your quote is ready</h2>
            <p class="xq-sub">We've also emailed it to ${esc(state.email.trim())}. It's subject to a home survey.</p>
            <a class="xq-btn xq-btn-block" href="${esc(state.quoteUrl)}" target="_blank" rel="noopener">View my quote</a>
            <button type="button" class="xq-link" data-action="restart">Start again</button>
          </div>`;
        }
        return `<div class="xq-done">
            <div class="xq-done-icon">${ICON_CHECK}</div>
            <h2 class="xq-title">${esc(c.thankYouTitle)}</h2>
            <p class="xq-sub">${esc(c.thankYouText)}</p>
            <button type="button" class="xq-link" data-action="restart">Start again</button>
          </div>`;
    }
  }

  /** Enable/disable buttons from current input without a full re-render (keeps focus). */
  function syncDisabled() {
    const valid: Record<string, boolean> = {
      postcode: POSTCODE_RE.test(state.postcode.trim()),
      address: state.address !== null,
      contact: state.name.trim().length > 1 && EMAIL_RE.test(state.email.trim()) && state.consent,
      details: isComplete(state.details),
    };
    root.querySelectorAll<HTMLButtonElement>("[data-needs]").forEach((b) => {
      b.disabled = state.busy || !valid[b.dataset.needs!];
    });
  }

  function go(step: Step) {
    opts.onStep?.(step);
    if (step === "contact") api.track?.("contact");
    state.step = step;
    state.error = null;
    render();
  }

  // ─── Actions ───────────────────────────────────────────────────────────────

  async function findAddresses() {
    state.busy = true;
    state.error = null;
    render();
    try {
      state.addresses = await api.searchAddresses(state.postcode.trim());
      state.address = null;
      state.busy = false;
      if (state.addresses.length === 0) {
        state.error = "We couldn't find any addresses for that postcode.";
        render();
      } else go("address");
    } catch {
      state.busy = false;
      state.error = "Something went wrong looking up your postcode. Please try again.";
      render();
    }
  }

  /** Fetch the EPC for an address and pre-select its answers (keeps any the homeowner already chose). */
  async function applyLookup(address: Address) {
    try {
      state.lookup = await api.lookupProperty(address);
    } catch {
      state.lookup = null; // treat a failed lookup like "no EPC": the homeowner answers manually
    }
    state.details = { ...(state.lookup?.details ?? {}), ...state.details };
  }

  /** Address chosen → look up its EPC → "About your home" with answers pre-selected. */
  async function lookupProperty() {
    if (!state.address) return;
    if (state.lookup === undefined) {
      state.busy = true;
      render();
      await applyLookup(state.address);
      state.busy = false;
    }
    go("details");
  }

  async function calculate() {
    state.busy = true;
    render();
    try {
      state.quote = await api.getQuote(state.address!, state.details as PropertyDetails);
      state.busy = false;
      go("quote");
    } catch {
      state.busy = false;
      state.error = "We couldn't calculate an estimate right now. Please try again.";
      render();
    }
  }

  async function submitLead() {
    state.busy = true;
    render();
    try {
      const lead = {
        address: state.address!,
        details: state.details as PropertyDetails,
        quote: state.quote!,
        name: state.name.trim(),
        email: state.email.trim(),
        phone: state.phone.trim(),
        consent: state.consent,
      };
      if (state.wantsQuote && api.requestQuote) {
        // No quote possible (e.g. no option fits): it went as a callback request instead
        state.quoteUrl = (await api.requestQuote(lead)).url;
      } else {
        await api.submitLead(lead);
      }
      state.busy = false;
      go("done");
    } catch {
      state.busy = false;
      state.error = "We couldn't send your details. Please try again.";
      render();
    }
  }

  // ─── Event delegation (one set of listeners for every re-render) ─────────────

  function onClick(e: Event) {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-action]");
    if (!el) return;
    const back: Partial<Record<Step, Step>> = { address: "postcode", details: "address" };
    switch (el.dataset.action) {
      case "open":
        state.open = true;
        api.track?.("opened");
        return render();
      case "close":
        state.open = false;
        return render();
      case "pick-address":
        state.address = state.addresses.find((a) => a.id === el.dataset.id) ?? null;
        // A different home: forget the previous EPC result and answers
        state.lookup = undefined;
        state.details = {};
        // Update the selection in place: a full re-render would jump a scrolled
        // address list back to the top
        root.querySelectorAll<HTMLElement>(".xq-option").forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.id === state.address?.id)));
        return syncDisabled();
      case "to-details":
        return void lookupProperty();
      case "chip": {
        const f = el.dataset.field as keyof PropertyDetails;
        const v = el.dataset.value!;
        state.details = { ...state.details, [f]: f === "bedrooms" ? Number(v) : v } as PropertyDetails;
        return render();
      }
      case "to-quote":
        return void calculate();
      case "to-contact":
        state.wantsQuote = false;
        return go("contact");
      case "to-quote-contact":
        state.wantsQuote = true;
        return go("contact");
      case "back":
        return go(back[state.step] ?? "postcode");
      case "restart":
        Object.assign(state, { postcode: "", addresses: [], address: null, lookup: undefined, details: {}, quote: null, wantsQuote: false, quoteUrl: null, name: "", email: "", phone: "", consent: false });
        return go("postcode");
    }
  }

  function onInput(e: Event) {
    const el = e.target as HTMLInputElement;
    const model = el.dataset.model;
    if (!model) return;
    if (model === "consent") state.consent = el.checked;
    else (state as unknown as Record<string, string>)[model] = el.value;
    syncDisabled();
  }

  function onSubmit(e: Event) {
    e.preventDefault();
    const form = (e.target as HTMLElement).dataset.form;
    if (form === "postcode" && POSTCODE_RE.test(state.postcode.trim())) void findAddresses();
    if (form === "contact") void submitLead();
  }

  shadow.addEventListener("click", onClick);
  shadow.addEventListener("input", onInput);
  shadow.addEventListener("change", onInput);
  shadow.addEventListener("submit", onSubmit);
  render();

  return {
    update(next) {
      branding = next;
      render();
    },

    async goTo(target) {
      // Fill whatever earlier steps would have collected, using the api (mock in preview)
      const step = target as Step;
      const i = STEPS.indexOf(step);
      if (i < 0) return;
      if (i >= 1 && state.addresses.length === 0) {
        state.postcode = "SW1A 1AA";
        state.addresses = await api.searchAddresses(state.postcode);
      }
      if (i >= 1 && !state.address) state.address = state.addresses[0] ?? null;
      if (i >= 2 && state.lookup === undefined && state.address) await applyLookup(state.address);
      if (i >= 3 && !isComplete(state.details)) state.details = { propertyType: "semi-detached", bedrooms: 3, heating: "gas", ...state.details };
      if (i >= 3 && !state.quote && state.address) state.quote = await api.getQuote(state.address, state.details as PropertyDetails);
      if (i >= 4 && !state.name) Object.assign(state, { name: "Alex Morgan", email: "alex@example.com", consent: true });
      state.open = true;
      go(step);
    },

    destroy() {
      shadow.removeEventListener("click", onClick);
      shadow.removeEventListener("input", onInput);
      shadow.removeEventListener("change", onInput);
      shadow.removeEventListener("submit", onSubmit);
      shadow.innerHTML = "";
    },
  };
}
